import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { ArrowLeft, ChevronDown, X } from "lucide-react";
import { useAuth,deleteCookie, } from "../../contexts/auth-context";
import { useToast } from "../../components/ui/use-toast";
import { IPatient } from "../../types";
import { usePatientsByMobile } from "../../lib/hooks/doctors/use-patients-by-mobile";
import { usePatientHealthRecords } from "../../lib/hooks/doctors/use-patient-health-records";
import HealthRecordCard from "./health-record-card";
import { useUpdatePatient } from "../../lib/hooks/patient/use-update-patient";
import { calculateBMI } from "../../lib/utils";

const MAX_ENTITY = 3;


function PatientProfile() {
  const { patientId } = useParams();
  const { patients } = usePatientsByMobile({});
  const { mobile, setMobile } = useAuth();
  const { toast } = useToast();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<IPatient | null>(null);
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");
  const [isEditingHeight, setIsEditingHeight] = useState(false);
  const [isEditingWeight, setIsEditingWeight] = useState(false);
  const [initialHeight, setInitialHeight] = useState("");
  const [initialWeight, setInitialWeight] = useState("");
  const [bmi, setBmi] = useState<string>("");
const [isEditingBmi, setIsEditingBmi] = useState<boolean>(false);
const [initialBmi, setInitialBmi] = useState<string>("");
  const { handleUpdatePatient } = useUpdatePatient();



  const { healthRecords = [] } = usePatientHealthRecords({
    name: selectedPatient?.name ?? "",
    mobile: mobile ?? "",
  });
  // console.log("Health records" , healthRecords);
  const [paginatedHealthRecords, setPaginatedHealthRecords] = useState(
    healthRecords.slice(0, MAX_ENTITY),
  );

  useEffect(() => {
    if (
      paginatedHealthRecords.length !==
      healthRecords.slice(0, MAX_ENTITY).length
    ) {
      setPaginatedHealthRecords(healthRecords.slice(0, MAX_ENTITY));
    }
  }, [healthRecords]);


  useEffect(() => {
    if (patients && patientId) {
      const patient = patients.find((p) => p.patientId === parseInt(patientId));
      if (patient) {
        setSelectedPatient(patient);
        setHeight(patient.height || "");
        setWeight(patient.weight || "");
        setInitialHeight(patient.height || "");
        setInitialWeight(patient.weight || "");
      }
    }
  }, [patients, patientId]);


  useEffect(() => {
    if (patients && patientId) {
      const patient = patients.find((p) => p.patientId === parseInt(patientId));
      if (patient) {
        setSelectedPatient(patient);
        setHeight(patient.height || "");
        setWeight(patient.weight || "");
        setInitialHeight(patient.height || "");
        setInitialWeight(patient.weight || "");
        const calculatedBmi = patient.height && patient.weight ? calculateBMI(Number(patient.height), Number(patient.weight)) : "";
        setBmi(calculatedBmi || "N/A");
        setInitialBmi(calculatedBmi || "N/A");
      }
    }
  }, [patients, patientId]);
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // console.log(selectedPatient);

  function handleSignout() {
    setMobile(null);
    deleteCookie("authToken");
    localStorage.removeItem("selectedPatient");
    localStorage.removeItem("mobile");
    toast({
      title: "Signed out",
      description: "You have been signed out successfully",
    });
  }



  const saveUpdatedPatient = () => {
    if (selectedPatient) {
      handleUpdatePatient(selectedPatient, height, weight);
      setIsEditingHeight(false);
      setIsEditingWeight(false);
      setInitialHeight(height);
      setInitialWeight(weight);
    }
  }

  const cancelEditHeight = () => {
    setHeight(initialHeight);
    setIsEditingHeight(false);
    
    // Recalculate BMI after canceling height edit
    if (initialHeight && weight && weight !== "N/A") {
      setBmi(calculateBMI(Number(initialHeight), Number(weight)));
    } else {
      setBmi("N/A");
    }
  };
  
  const cancelEditWeight = () => {
    setWeight(initialWeight);
    setIsEditingWeight(false);
    
    // Recalculate BMI after canceling weight edit
    if (height && height !== "N/A" && initialWeight) {
      setBmi(calculateBMI(Number(height), Number(initialWeight)));
    } else {
      setBmi("N/A");
    }
  };


  if (!selectedPatient) {
    return <p>Loading.....</p>;
  }

  return (
    <div className="relative">
      <div className="p-4 bg-white shadow-lg w-full max-w-md mx-auto mt-8 lg:max-w-3xl lg:mt-16">
        <div className="flex items-center">
          <Link to="/doctors" className="p-2">
            <ArrowLeft className="w-6 h-6 text-gray-700" />
          </Link>
          <p className="ml-2 text-lg font-semibold">Patient Profile</p>
        </div>

        <div className="mt-4 flex items-center">
          <div className="bg-purple-600 text-white w-12 h-12 flex items-center justify-center rounded-full text-lg font-bold">
            {selectedPatient.name[0]}
          </div>
          <div className="ml-3 flex items-center justify-between w-full">
            <div>
              <h2 className="text-xl font-semibold">{selectedPatient.name}</h2>
              <div className="flex items-center">
                <p className="text-gray-600">
                  {selectedPatient.age} Years,{" "}
                  {selectedPatient.gender === "M" ? "Male" : "Female"}
                </p>
              </div>
            </div>
          </div>
        </div>

     
       {/* Editable Height and Weight */}
<div className="mt-6 space-y-2">
  <div className="flex items-center justify-between">
    <p className="font-medium">
      Height:{" "}
      {isEditingHeight ? (
        <input
          type="text"
          value={height}
          onChange={(e) => {
            let value = e.target.value;
            value = value.replace(/\D/g, "").slice(0, 3);
            setHeight(value);
            if (!isEditingBmi) {
              if (value && weight && weight !== "N/A") {
                setBmi(calculateBMI(Number(value), Number(weight)));
              } else {
                setBmi("N/A");
              }
            }
          }}
          className="ml-2 border p-1 rounded"
        />
      ) : (
        <>
        <span>{height || "N/A"}</span>
        {height && height !== "N/A" ? <span> cm </span> : null}
        </>
      )}
    </p>
    <Button
      variant="ghost"
      className="ml-2"
      onClick={() => {
        if (isEditingHeight) cancelEditHeight();
        else setIsEditingHeight(true);
      }}
      >
      {isEditingHeight ? "Cancel" : "Edit"}
    </Button>
  </div>
  <div className="flex items-center justify-between">
    <p className="font-medium">
      Weight:{" "}
      {isEditingWeight ? (
        <input
          type="text"
          value={weight}
          onChange={(e) => {
            let value = e.target.value;
            value = value.replace(/\D/g, "").slice(0, 3);
            setWeight(value);
            if (!isEditingBmi) {
              if (value && height && height !== "N/A") {
                setBmi(calculateBMI(Number(height), Number(value)));
              } else {
                setBmi("N/A");
              }
            }
          }}
          className="ml-2 border p-1 rounded"
        />
      ) : (
        <>
          <span>{weight || "N/A"}</span>
          {weight && weight !== "N/A" ? <span> kg</span> : null}
        </>
      )}
    </p>
    <Button
      variant="ghost"
      className="ml-2"
      onClick={() => {
        if (isEditingWeight) cancelEditWeight();
        else setIsEditingWeight(true);
      }}
      >
      {isEditingWeight ? "Cancel" : "Edit"}
    </Button>
  </div>
</div>

<div className="flex items-center justify-between mt-3">
  <p className="font-medium">
    BMI:{" "}
    {isEditingBmi ? (
      <input
        type="text"
        //disabled={true}
        value={bmi === "N/A" ? "" : bmi}
        onChange={(e) => {
          const value = e.target.value;
          if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
            setBmi(value || "N/A");
            setIsEditingBmi(value !== "");
          }
        }}
        className="ml-7 border p-1 rounded"
      />
    ) : (
      <>
        <span>{bmi}</span>
        {bmi && bmi !== "N/A" ? <span> kg/m²</span> : null}
      </>
    )}
  </p>
  {/* <Button
    variant="ghost"
    className="ml-2"
    onClick={() => {
      if (isEditingBmi) {
        setBmi(initialBmi);
        setIsEditingBmi(false);
      } else {
        setIsEditingBmi(true);
      }
    }}
  >
    {isEditingBmi ? "Cancel" : "Edit"}
  </Button> */}
</div>
{bmi === "N/A" && (
  <p className="text-xs text-black/70 mt-1 whitespace-nowrap">
    Enter height and weight both to autocalculate BMI
  </p>
)}


        {/* Allergies, illnesses, lifestyle */}
        <div className="mt-6">
          <h3 className="font-medium">Allergies</h3>
          <p>{selectedPatient.allergies || "No Allergies"}</p>
        </div>

        <div className="mt-4">
          <h3 className="font-medium">Past Illnesses</h3>
          <p>{selectedPatient.pastIllness || "No Past Illnesses"}</p>
        </div>

    
        {/* Health Records */}
        <div className="mt-6 flex justify-between items-center">
          <h3 className="font-medium">Health Records</h3>
          {healthRecords.length > MAX_ENTITY &&
            paginatedHealthRecords.length !== healthRecords.length && (
              <Button
                variant="link"
                className="text-xs !font-bold sm:text-sm"
                onClick={() => setPaginatedHealthRecords(healthRecords)}
              >
                View all records
              </Button>
            )}
        </div>
        <ul className="space-y-2 mt-4">
        {Array.isArray(paginatedHealthRecords) ? (
                paginatedHealthRecords.map((record) => (
                  <HealthRecordCard key={record.recordId} {...record} />
                ))
              ) : (
                <p>No health records available.</p>
              )}
        </ul>

        <Button variant="default" className="w-full mt-6 bg-teal-600 text-white py-2" onClick={saveUpdatedPatient}>
          Save
        </Button>
      </div>

      {/* Sidebar */}
      {isSidebarOpen && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-40 z-40"
            onClick={toggleSidebar}
          ></div>

          <div className="fixed top-0 right-0 w-64 h-full bg-white shadow-lg z-50">
            <div className="p-6">
              <div className="flex items-center">
                <div className="bg-purple-600 text-white w-12 h-12 flex items-center justify-center rounded-full text-lg font-bold">
                  {selectedPatient.name[0]}
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold">{selectedPatient.name}</h3>
                  <p className="text-gray-500 text-sm">{selectedPatient.mobile}</p>
                </div>
              </div>
              {/* Sidebar links */}
              <div className="mt-8 space-y-4">
                <Link to="/patients" className="block text-blue-600 text-sm">
                  View All Patients
                </Link>
                <Link to="/add-patient" className="block text-blue-600 text-sm">
                  Add New Patient
                </Link>
                <Link to="/health-records" className="block text-blue-600 text-sm">
                  Health Records
                </Link>
                <Link to="/support" className="block text-blue-600 text-sm">
                  Help and Support
                </Link>
                {!mobile ? (
                  <Link to="/verify-patient" className="block text-blue-600 text-sm">
                    Sign in
                  </Link>
                ) : (
                  <button
                    onClick={handleSignout}
                    className="block text-blue-600 text-sm"
                  >
                    Sign out
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default PatientProfile;
